import React, { useState, useCallback } from "react";
import { useEffect } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import CurrencyFormat from "react-currency-format";
import { Modal } from "react-bootstrap";
import axiosInstance from "../../utils/axiosInstance";
import Pagination from "../../components/pagination/pagination";
import { useDateTime } from "../../components/Helper/DateTime";
import ButtonBasic from "../../components/Buttons/ButtonBasic";
import LoaderComponent from "../../components/Spinner/LoaderComponent";
import { DateTimeFormat } from "../../utils/formatDateTime";
import { baseUrl } from "../../configuration/apiUrl";
import Toast from "../../components/Toast/Toast";
import ShowForPermission from "../../utils/permissionsWrapper";

const formatNumber = (row, isColor) => {
  return (
    <CurrencyFormat
      value={row !== null ? row : "-"}
      displayType={"text"}
      thousandSeparator={true}
      style={{ color: isColor }}
    />
  );
};

export const DataCollection = ({
  refresh,
  filter,
  pageFilter,
  setPageFilter,
  dataCollectionId,
  setDataCollectionId,
}) => {
  const { dateTime, setRefresh } = useDateTime();
  const [data, setData] = useState([]);
  const [singelDCData, setSingleDCData] = useState([]);
  const [rowEventData, setRowEventData] = useState({
    fileId: null,
    showModal: false,
    interfaceName: "",
  });
  const [rowId, setRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sort, setSort] = useState({ sort_by: "", sort_order: "" });
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(10);
  const [modalTotalPages, setModalTotalPages] = useState(0);
  const [modalTotalElements, setModalTotalElements] = useState(0);
  const [modalSort, setModalSort] = useState({ sort_by: "", sort_order: "" });

  // Download file function
  const downloadFile = async (interfaceId, fileName, isException) => {
    try {
      const url =
        baseUrl +
        `/interfaces/download-staging-file?interface_id=${interfaceId}&file_name=${fileName}&is_exception=${isException}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("userData")).accessToken
          }`, // Add auth token if needed
        },
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      // Extract filename from header
      const contentDisposition = response.headers.get("content-disposition");
      let downloadedFileName = fileName;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename\*?=UTF-8''(.+)/i
        );
        if (filenameMatch && filenameMatch?.[1]) {
          // Decode URI encoded filename
          downloadedFileName = decodeURIComponent(filenameMatch?.[1]);
        } else {
          // Fallback: try to extract filename without UTF-8 encoding
          const fallbackMatch = contentDisposition.match(/filename="(.+)"/i);
          if (fallbackMatch && fallbackMatch?.[1]) {
            downloadedFileName = fallbackMatch?.[1];
          }
        }
      }

      // If no file extension found, default to .txt
      if (!downloadedFileName.includes(".")) {
        downloadedFileName += ".txt";
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = downloadedFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      Toast(
        error?.response?.data?.message?.description ||
          error?.message ||
          "Error",
        "error"
      );
    }
  };

  const onSortTable = (type, newState) => {
    setPage(1);
    setSort({ sort_by: newState.sortField, sort_order: newState.sortOrder });
  };

  const onSortSingleTable = (type, newState) => {
    setPage(1);
    setModalSort({
      sort_by: newState.sortField,
      sort_order: newState.sortOrder,
    });
  };

  const getDataCollection = useCallback(
    (filter, flag) => {
      const order = `&sort_by=${sort.sort_by}&sort_order=${sort.sort_order}`;
      let url = `/interfaces/status?page=${
        flag ? 0 : page - 1
      }&size=${pageSize}${
        sort.sort_by && sort.sort_order ? order : ""
      }${filter}`;
      axiosInstance
        .get(url)
        .then((res) => {
          if (res.message.status !== "200") {
            setData([]);
            setLoading(false);
            setTotalElements(0);
          } else {
            setData(res?.data?.interfaceStatus || []);
            setPage(res?.page?.number + 1 || 1);
            setTotalPages(res?.page?.totalPages || 0);
            setTotalElements(res?.page?.totalElements || 0);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setLoading(false);
        });
    },
    // eslint-disable-next-line
    [page, pageSize, sort, filter]
  );

  const getSingleDCDetails = useCallback(
    (flag, id, filter) => {
      setLoadingModal(true);
      const order = `&sort_by=${modalSort.sort_by}&sort_order=${modalSort.sort_order}`;
      let url = `/interfaces/${id}/files?page=${
        flag ? 0 : modalPage - 1
      }&size=${modalPageSize}${filter}${
        modalSort.sort_by && modalSort.sort_order ? order : ""
      }`;
      axiosInstance
        .get(url)
        .then((res) => {
          if (res.message.status !== "200") {
            setSingleDCData([]);
            setLoadingModal(false);
            setModalTotalElements(0);
          } else {
            setSingleDCData(res?.data?.interfaceFiles || []);
            setModalPage(res?.page?.number + 1 || 1);
            setModalTotalPages(res?.page?.totalPages || 0);
            setModalTotalElements(res?.page?.totalElements || 0);
            setLoadingModal(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setLoadingModal(false);
        });
    },
    [modalPage, modalPageSize, modalSort]
  );

  useEffect(() => {
    setLoading(true);
    getDataCollection(filter, false);
  }, [getDataCollection, refresh]);

  useEffect(() => {
    let timerId = setInterval(() => {
      getDataCollection(filter, false);
    }, 5000);
    return () => {
      clearInterval(timerId);
    };
  });

  useEffect(() => {
    if (pageFilter) {
      setLoading(true);
      getDataCollection(filter, true);
      setPageFilter(false);
    }
  }, [pageFilter]);

  useEffect(() => {
    if (singelDCData && rowId !== null) {
      getSingleDCDetails(false, rowId, filter);
    } else {
      return false;
    }
  }, [modalPage, modalPageSize, modalSort, getSingleDCDetails]);

  useEffect(() => {
    if (dataCollectionId) {
      setRowId(dataCollectionId);
      setRowEventData({
        ...rowEventData,
        showModal: dataCollectionId,
      });
      getSingleDCDetails(true, dataCollectionId, filter);
    }
  }, [dataCollectionId]);

  const columnsDataCollection = [
    { hidden: true, dataField: "interfaceId", text: "ID" },
    {
      dataField: "interfaceName",
      text: "Interface Name",
      sort: true,
    },
    {
      dataField: "runDatetime",
      text: "Last Batch Received",
      formatter: (cell) => (cell ? DateTimeFormat(dateTime, cell) : "-"),
      sort: true,
    },
    {
      dataField: "filesProcessed",
      text: " File Received",
      formatter: formatNumber,
    },
    {
      dataField: "filesDiscarded",
      text: "Files Discarded",
      formatter: (cell) => formatNumber(cell, cell > 0 ? "red" : ""),
    },
    {
      dataField: "recordProcessed",
      text: "Records Read",
      formatter: formatNumber,
    },
    {
      dataField: "recordFailed",
      text: "Records Failed",
      formatter: (cell) => formatNumber(cell, cell > 0 ? "red" : ""),
    },
    {
      dataField: "recordsInStaging",
      text: "Records In Staging",
      formatter: formatNumber,
    },
  ];

  // utility: get max digit length in a column
  const getMaxDigitWidth = (data, field, minDigits = 4) => {
    let maxDigits = minDigits;
    data?.forEach((row) => {
      const value = row?.[field];
      const digitCount = String(value ?? "").length;
      if (digitCount > maxDigits) {
        maxDigits = digitCount;
      }
    });
    return `${maxDigits}ch`; // consistent width for all rows in that column
  };

  // pre-calc widths
  const recordFailedWidth = getMaxDigitWidth(singelDCData, "recordFailed");
  const recordsInStagingWidth = getMaxDigitWidth(
    singelDCData,
    "recordsInStaging"
  );

  const singelDCColumns = [
    { hidden: true, dataField: "id", text: "ID" },
    {
      dataField: "filename",
      text: "File Name",
      style: { cursor: "default" },
    },
    {
      dataField: "runDatetime",
      text: "Run Date/Time",
      formatter: (cell) => (cell ? DateTimeFormat(dateTime, cell) : "-"),
      sort: true,
      style: { cursor: "default" },
    },
    {
      dataField: "jobEndDateTime",
      text: "Job End Date/Time",
      formatter: (cell) => (cell ? DateTimeFormat(dateTime, cell) : "-"),
      sort: true,
      style: { cursor: "default" },
    },
    {
      dataField: "recordProcessed",
      text: "Records Read",
      formatter: formatNumber,
      sort: true,
      style: { cursor: "default" },
    },
    {
      dataField: "recordFailed",
      text: "Records Failed",
      formatter: (cell, row) => {
        const formattedValue = formatNumber(cell, cell > 0 ? "red" : "");
        if (cell > 0) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  display: "inline-block",
                  minWidth: recordFailedWidth, // consistent width
                  textAlign: "left",
                }}
              >
                {formattedValue}
              </span>
              <ShowForPermission permission="download_staging_data_file_button">
                <button
                  className="primary-color"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(rowId, row.filename, true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    fontSize: "12px",
                  }}
                  title="Download failed records file"
                >
                  Download
                </button>
              </ShowForPermission>
            </div>
          );
        }
        return (
          <span
            style={{
              display: "inline-block",
              minWidth: recordFailedWidth,
              textAlign: "left",
            }}
          >
            {formattedValue}
          </span>
        );
      },
      sort: true,
      style: { cursor: "default" },
    },
    {
      dataField: "recordsInStaging",
      text: "Records In Staging",
      formatter: (cell, row) => {
        if (cell > 0) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  display: "inline-block",
                  minWidth: recordsInStagingWidth, // consistent width
                  textAlign: "left",
                }}
              >
                {formatNumber(cell)}
              </span>
              <ShowForPermission permission="download_staging_data_file_button">
                <button
                  className="primary-color"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(rowId, row.filename, false);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    fontSize: "12px",
                  }}
                  title="Download staging records file"
                >
                  Download
                </button>
              </ShowForPermission>
            </div>
          );
        }
        return (
          <span
            style={{
              display: "inline-block",
              minWidth: recordsInStagingWidth,
              textAlign: "left",
            }}
          >
            {formatNumber(cell)}
          </span>
        );
      },
      sort: true,
      style: { cursor: "default" },
    },
    {
      dataField: "jobStatus",
      text: "Job Status",
      sort: true,
      style: { cursor: "default" },
    },
    {
      dataField: "fileDiscardStatus",
      text: "File Discard Status",
      formatter: (cell) => {
        return cell ? cell : "-";
      },
      sort: true,
      style: { cursor: "default" },
    },
  ];

  const rowEvent = {
    onClick: (e, row) => {
      let id = row.interfaceId;
      setRowId(id);
      setRowEventData({
        ...rowEventData,
        fileId: id,
        showModal: true,
        interfaceName: row.interfaceName,
      });
      getSingleDCDetails(true, id, filter);
    },
  };

  return (
    <>
      <div className="row mt-3 text-select">
        <h5 className="title text-bright ml-3 mb-4">Data Collection</h5>
        <>
          <div className="col-12">
            <div className="overflow-y-auto">
              <div id="anchor1">
                <BootstrapTable
                  columns={columnsDataCollection}
                  bootstrap4={true}
                  keyField="id"
                  bordered={false}
                  classes="rs-table table-layout-auto table-custom-style table-custom-style"
                  wrapperClasses="overflow-y-auto fancy-scroll"
                  data={data}
                  rowEvents={rowEvent}
                  remote={{ sort: true }}
                  onTableChange={onSortTable}
                  noDataIndication={
                    loading ? <LoaderComponent /> : "No data found!"
                  }
                />
              </div>
            </div>
          </div>
          <div className="col-12 mb-3">
            <Pagination
              setPage={setPage}
              page={page}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalPages={totalPages}
              totalElements={totalElements}
              data={data || []}
            />
          </div>
        </>
      </div>

      <Modal
        backdrop={true}
        size="xl"
        show={rowEventData.showModal}
        onHide={() => {
          setRowEventData({ ...rowEventData, showModal: false });
          setSingleDCData([]);
        }}
        aria-labelledby="example-modal-sizes-title-lg"
      >
        <Modal.Header closeButton> {rowEventData?.interfaceName}</Modal.Header>
        <Modal.Body scrollable="true">
          <div
            className="row"
            style={{ opacity: !loadingModal ? "1" : "0.07" }}
          >
            <div className="col-12">
              <div className="d-flex justify-content-center">
                <div className="overflow-y-auto" style={{ cursor: "default" }}>
                  <BootstrapTable
                    bordered={false}
                    bootstrap4={true}
                    classes="rs-table table-layout-auto mb-0 table-custom-style"
                    wrapperClasses="overflow-y-auto mb-2 fancy-scroll"
                    keyField="id"
                    data={singelDCData}
                    columns={singelDCColumns}
                    remote={{ sort: true }}
                    onTableChange={onSortSingleTable}
                    noDataIndication={
                      loadingModal ? <LoaderComponent /> : "No data found!"
                    }
                  ></BootstrapTable>
                </div>
              </div>
            </div>
          </div>

          <Pagination
            setPage={setModalPage}
            page={modalPage}
            pageSize={modalPageSize}
            setPageSize={setModalPageSize}
            totalPages={modalTotalPages}
            totalElements={modalTotalElements}
            data={singelDCData || []}
          />
          <div className="d-flex justify-content-end align-items-center">
            <ButtonBasic
              title="Cancel"
              onClick={() => {
                setRowEventData({
                  ...rowEventData,
                  showModal: false,
                });
                setDataCollectionId(null);
              }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
